import {
  Box,
  ClickAwayListener,
  Icon,
  IconButton,
  MenuItem,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ApiError,
  DATE_TIME_FORMAT_FNS,
  DEFAULT_DATE_FORMAT_FNS,
  SchoolReEnrollmentResponse,
  StudentRegistration,
  useExportTermsAndConditions,
  useGetNotificationAttendanceSettingsQuery,
  useGetProductsToBeAssignedMutation,
} from '@schooly/api';
import {
  InviteNotAcceptedWarning,
  isReEnrollmentPendingRegistration,
  isReEnrollmentPendingStatus,
  SameAgeGroupWarning,
} from '@schooly/components/annual-roll-over';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { useFlag } from '@schooly/hooks/use-flag';
import {
  ArrowUpIcon,
  DeleteIcon,
  DocumentBlankIcon,
  DocumentIcon,
  DocumentSignedIcon,
  LogoutIcon,
  MoreIcon,
  SchoolIcon,
  SettingsIcon,
  SimpleButton,
  Spin,
  StudentIcon,
  TagSelect,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import React, { FC, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { useProfile } from '../../../context/profile/useProfile';
import { getDisplayStatus, getLeavingNoticeStatus } from '../../../helpers/registrations';
import useSchoolYears from '../../../hooks/useSchoolYears';
import {
  convertFormDataToAssignedProductSave,
  convertProductToAssignedProductUpdate,
} from '../../../pages/ProfileModal/tabs/ProfileModalPayers/StudentProductsModal/helpers';
import { AssignedProductUpdate } from '../../../pages/ProfileModal/tabs/ProfileModalPayers/StudentProductsModal/StudentProductsModalContent';
import { openFile } from '../../../utils/openFile';
import RegistrationCard, { RegistrationProps } from '../RegistrationCard';
import { StatusStringForRegistration } from '../RegistrationCard/helpers';
import {
  ProductAssignmentsConfirmModal,
  ProductAssignmentsDescription,
  ProductAssignmentsForm,
  ProductAssignmentsTitle,
} from '../StudentRegistration/ProductAssignmentsConfirmModal';
import { RegistrationLeavingNoticeInfo } from '../StudentRegistration/RegistrationLeavingNoticeInfo';
import {
  ReEnrollmentProps,
  useReEnrollmentActions,
} from '../StudentRegistration/useReEnrollmentActions';
import { ReEnrollmentTag } from '../StudentRegistration/useReEnrollmentStatuses';
import { getNextYearEnrollments } from '../StudentRegistration/utils';
export interface StudentRegistrationProps extends Omit<RegistrationProps, 'registration'> {
  enrollment: StudentRegistration;
  canRollover: boolean;
  onRollover: (reg: StudentRegistration) => void;
  onShowFamily: () => void;
  prevEnrollments: StudentRegistration[];
  refetchEnrollments: () => void;
  reEnrollmentData: SchoolReEnrollmentResponse;
  rejectedEnrollments: StudentRegistration[];
}

export const StudentRegistrationComponent: FC<StudentRegistrationProps> = ({
  enrollment,
  onClick,
  canRollover,
  onRollover,
  onShowFamily,
  prevEnrollments,
  refetchEnrollments,
  reEnrollmentData,
  rejectedEnrollments,
}) => {
  const [opened, open, close] = useFlag();
  const { currentUser, schoolId, permissions } = useAuth();
  const navigate = useNavigate();
  const { user } = useProfile();
  const { showError } = useNotifications();
  const getProductAssignments = useGetProductsToBeAssignedMutation();
  const { schoolYears } = useSchoolYears();

  const [productAssignments, setProductAssignments] = useState<{
    products: AssignedProductUpdate[];
    yearId: string;
  }>();

  const { mutateAsync: exportDocument, isLoading: isExporting } = useExportTermsAndConditions();

  const isAdmin = permissions?.includes('school_admin');

  const { signature: termsAndConditionsSignature, terms_and_conditions: termsAndConditions } =
    enrollment.terms_and_conditions_consent ?? {};
  const isTermsAndConditionsSigned = !!termsAndConditionsSignature;
  const isTermsAndConditionsDisabled = !enrollment.terms_and_conditions_consent;

  const {
    handleAcceptReEnrollment,
    handleRejectReEnrollment,
    handleDeleteEnrollment,
    isReEnrollmentUpdating,
  } = useReEnrollmentActions({
    fetchEnrollments: refetchEnrollments,
    reEnrollmentData,
  });

  const { data: notificationSettings } = useGetNotificationAttendanceSettingsQuery(
    { schoolId: schoolId ?? '' },
    { enabled: !!schoolId && !!productAssignments?.products?.length, refetchOnMount: 'always' },
  );

  const onAcceptReEnrollment = useCallback(
    async (props: ReEnrollmentProps) => {
      if (!schoolId || !user?.relation_id) return;

      try {
        const response = await getProductAssignments.mutateAsync({
          school_id: schoolId,
          // https://schooly.atlassian.net/browse/TR-9118?focusedCommentId=22142
          enrollments: [],
          relation_id: user.relation_id,
        });

        const hasProductAssignments = response.products.some((p) => p.length);

        if (hasProductAssignments) {
          setProductAssignments({
            products: response.products[0].map(convertProductToAssignedProductUpdate),
            yearId: props.enrollment.school_year.id,
          });
          return;
        }

        handleAcceptReEnrollment(props);
      } catch (error) {
        showError(error as ApiError);
      }
    },
    [getProductAssignments, handleAcceptReEnrollment, schoolId, showError, user?.relation_id],
  );

  const handleConfirmProductAssignments = useCallback(
    async (data: ProductAssignmentsForm) => {
      if (!user) return;
      const products = data.products[0].map(convertFormDataToAssignedProductSave) ?? [];

      await handleAcceptReEnrollment({
        enrollment,
        prevEnrollments,
        user,
        product_assignments: products,
      });

      setProductAssignments(undefined);
    },
    [enrollment, handleAcceptReEnrollment, prevEnrollments, user],
  );

  const handleCloseProductAssignments = () => {
    setProductAssignments(undefined);
  };

  const contextActions = useRef([
    {
      labelTextId: 'school-annualRollover-AcceptReEnrollment',
      onClick: onAcceptReEnrollment,
      icon: <StudentIcon />,
    },
    {
      labelTextId: 'school-annualRollover-RejectReEnrollment',
      onClick: handleRejectReEnrollment,
      icon: <LogoutIcon className="reset-svg-currentColor" />,
    },
  ]);
  const { $t } = useIntl();
  const { schoolMembership } = useProfile();

  const isInviteAccepted =
    schoolMembership &&
    'parents_invite_accepted' in schoolMembership &&
    !!schoolMembership.parents_invite_accepted;

  const isPendingRegistration = isReEnrollmentPendingRegistration(enrollment);

  const displayStatus = useMemo(
    () => (enrollment.statuses.length ? getDisplayStatus(enrollment.statuses) : null),
    [enrollment.statuses],
  );

  const leavingNoticeStatus = useMemo(
    () => (enrollment.statuses.length ? getLeavingNoticeStatus(enrollment.statuses) : null),
    [enrollment.statuses],
  );

  const isReEnrollmentStatus = displayStatus?.school_property.source?.type === 're_enrollment';

  const isPendingStatus = Boolean(
    displayStatus && isReEnrollmentPendingStatus(displayStatus.school_property),
  );

  const isExpiredStatus = Boolean(
    isReEnrollmentStatus && displayStatus?.school_property.category?.final,
  );

  const isLeftByReject = useMemo(() => {
    const nextYearRejectedEnrollments = getNextYearEnrollments({
      schoolYears,
      enrollmentYearId: enrollment.school_year.id,
      enrollments: rejectedEnrollments,
    });

    return nextYearRejectedEnrollments.length > 0;
  }, [enrollment?.school_year.id, rejectedEnrollments, schoolYears]);

  const isLoading = isReEnrollmentUpdating || getProductAssignments.isLoading;
  const hasRegistrationManagerPermission = permissions?.includes('registration_manager');
  const showReEnrollmentActions =
    isPendingRegistration && !isLoading && hasRegistrationManagerPermission;

  const shouldShowLeavingStatus =
    !!user &&
    !!leavingNoticeStatus &&
    leavingNoticeStatus.applies_from > format(new Date(), DEFAULT_DATE_FORMAT_FNS);

  const openDocument = useCallback(
    async (id: string) => {
      try {
        const { data } = await exportDocument({ id });
        if (data) {
          openFile(data);
        }
      } catch (error) {
        showError(error as ApiError);
      }
    },
    [exportDocument, showError],
  );

  const renderTermsAndConditionsTooltip = () => {
    //T&C was signed
    if (isTermsAndConditionsSigned && termsAndConditions) {
      return (
        <Stack alignItems="flex-start">
          <Typography color="text.secondary">
            {format(new Date(termsAndConditionsSignature.signed_at), DATE_TIME_FORMAT_FNS)}
          </Typography>

          <Typography>
            {$t(
              { id: 'school-TermsAndConditions-Signed' },
              {
                name: (
                  <Typography
                    color="primary.main"
                    component={Link}
                    to={`/parents/${termsAndConditionsSignature?.signed_by.relation_id}`}
                    sx={{ ':hover': { textDecoration: 'underline', color: 'primary.main' } }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {getUserFullName(termsAndConditionsSignature?.signed_by)}
                  </Typography>
                ),
              },
            )}
          </Typography>
          <SimpleButton
            sx={{ mt: 1.25 }}
            startIcon={isExporting ? <Spin /> : <DocumentIcon />}
            onClick={(e) => {
              e.stopPropagation();
              openDocument(termsAndConditions.id);
            }}
          >
            {$t({ id: 'school-TermsAndConditions-ViewDocument' })}
          </SimpleButton>
        </Stack>
      );
    }

    //T&C was disabled during re-enrollment
    if (enrollment.type === 're_enrollment' && isTermsAndConditionsDisabled) {
      return (
        <Stack alignItems="flex-start" gap={1.25}>
          <Typography>{$t({ id: 'school-TermsAndConditions-SigningDisabled' })}</Typography>
          {isAdmin && (
            <SimpleButton
              startIcon={<SettingsIcon />}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/settings/rollover_and_leaving');
              }}
            >
              {$t({ id: 'school-Settings-GoToSettings' })}
            </SimpleButton>
          )}
        </Stack>
      );
    }

    //registration was created by school
    return $t({ id: 'school-TermsAndConditions-CreatedBySchool' });
  };

  const renderTermsAndConditionsIcon = () => {
    //T&C was signed
    if (isTermsAndConditionsSigned) {
      return <DocumentSignedIcon />;
    }

    //T&C was disabled during re-enrollment
    if (enrollment.type === 're_enrollment' && isTermsAndConditionsDisabled) {
      return <DocumentBlankIcon />;
    }

    //registration was created by school
    return <SchoolIcon />;
  };

  const renderActions = () => {
    const sameAgeGroupWarning = enrollment.same_age_group ? (
      <SameAgeGroupWarningComponent
        canRollover={canRollover}
        onRollover={() => onRollover(enrollment)}
      />
    ) : null;

    const termsAndConditionsConsent = isPendingRegistration ? null : (
      <Tooltip
        title={renderTermsAndConditionsTooltip()}
        componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
      >
        {
          <Icon
            sx={{ color: 'common.grey2', '.RegistrationCard:hover &': { color: 'primary.main' } }}
          >
            {renderTermsAndConditionsIcon()}
          </Icon>
        }
      </Tooltip>
    );

    if (!sameAgeGroupWarning && !termsAndConditionsConsent) return null;

    return (
      <Stack direction="row" gap={2.5}>
        {termsAndConditionsConsent}
        {sameAgeGroupWarning}
      </Stack>
    );
  };

  return (
    <RegistrationCardWrapper
      isPendingRegistration={isPendingRegistration}
      isMenuActive={opened}
      isPendingStatus={isPendingStatus}
      isExpiredStatus={isExpiredStatus}
    >
      <RegistrationCard
        key={enrollment.id}
        registration={enrollment}
        onClick={!isPendingRegistration ? onClick : undefined}
        actionButton={renderActions()}
        canEdit={!isPendingRegistration}
        renderStatus={() => (
          <Stack direction="row" gap={1}>
            <StatusStringForRegistration
              registration={enrollment}
              statusInfo={
                <Stack direction="row">
                  {!isInviteAccepted && isPendingStatus && (
                    <InviteNotAcceptedWarning onShowFamily={onShowFamily} />
                  )}
                  {isLeftByReject ? (
                    <ReEnrollmentTag>
                      {$t({ id: 'students-ReEnrollment-NotReEnrolled' })}
                    </ReEnrollmentTag>
                  ) : null}
                </Stack>
              }
            >
              {!!leavingNoticeStatus?.leaving_reason?.title && (
                <TagSelect
                  sx={{
                    maxWidth: 100,
                    mr: 1,
                    '.MuiChip-label': {
                      mr: 0,
                    },
                  }}
                  label={
                    <TypographyWithOverflowHint>
                      {leavingNoticeStatus.leaving_reason.title}
                    </TypographyWithOverflowHint>
                  }
                />
              )}
            </StatusStringForRegistration>
          </Stack>
        )}
        renderLeavingNotice={() =>
          shouldShowLeavingStatus && (
            <RegistrationLeavingNoticeInfo user={user} status={leavingNoticeStatus} />
          )
        }
      >
        {isLoading && (
          <Icon>
            <Spin />
          </Icon>
        )}
        {showReEnrollmentActions && (
          <>
            {isPendingStatus ? (
              <ClickAwayListener
                onClickAway={() => {
                  if (opened) close();
                }}
              >
                <div>
                  <Tooltip
                    open={opened}
                    disableTouchListener
                    componentsProps={{
                      tooltip: {
                        sx: (theme) => ({
                          padding: theme.spacing(1),
                          maxWidth: 215,
                        }),
                      },
                    }}
                    title={
                      <>
                        {contextActions.current.map((a) => (
                          <MenuItem
                            key={a.labelTextId}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!currentUser) return;
                              a.onClick({ enrollment, prevEnrollments, user: currentUser });
                              close();
                            }}
                            sx={(theme) => ({
                              m: 0,
                              py: theme.spacing(0.5),
                              px: theme.spacing(1.25),
                              borderRadius: theme.spacing(0.5),
                            })}
                          >
                            <Stack direction="row" gap={0.75}>
                              <Icon color="primary">{a.icon}</Icon>
                              <Typography variant="h3">{$t({ id: a.labelTextId })}</Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </>
                    }
                  >
                    <IconButton
                      className="menuIcon"
                      sx={{
                        visibility: 'hidden',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        open();
                      }}
                    >
                      <MoreIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </ClickAwayListener>
            ) : (
              <IconButton
                className="menuIcon"
                inverse
                sx={{
                  visibility: 'hidden',
                }}
                onClick={() => handleDeleteEnrollment(enrollment.id)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        )}
      </RegistrationCard>
      {!!productAssignments?.products?.length && user && (
        <ProductAssignmentsConfirmModal
          onClose={handleCloseProductAssignments}
          onConfirm={handleConfirmProductAssignments}
          users={[user]}
          products={[productAssignments.products]}
          saving={isReEnrollmentUpdating}
          title={
            <ProductAssignmentsTitle
              hasChangesToConfirm={false}
              hasProductAssignments={productAssignments.products.length > 0}
            />
          }
          description={
            <ProductAssignmentsDescription
              hasProductAssignments={productAssignments.products.length > 0}
              notificationEmail={notificationSettings?.internal_emails?.invoicing_email}
            />
          }
          yearId={productAssignments.yearId}
        />
      )}
    </RegistrationCardWrapper>
  );
};

type RegistrationCardWrapperProps = {
  isMenuActive: boolean;
  isPendingRegistration: boolean;
  isPendingStatus: boolean;
  isExpiredStatus: boolean;
};

const RegistrationCardWrapper = styled(Box)<RegistrationCardWrapperProps>(
  ({ theme, isPendingRegistration, isMenuActive, isPendingStatus, isExpiredStatus }) => {
    const pendingColor = isPendingStatus ? theme.palette.common.orange : undefined;
    const expiredColor = isExpiredStatus ? theme.palette.error.main : undefined;
    const pendingBg = isPendingStatus ? theme.palette.common.orange5 : undefined;
    const expiredBg = isExpiredStatus ? theme.palette.error.superLight : undefined;

    const textColor = pendingColor || expiredColor;
    const bgColor = pendingBg || expiredBg;
    return {
      '.Card': {
        ...(isPendingRegistration && {
          border: textColor ? `1px solid ${textColor} !important` : undefined,
          borderRadius: '8px !important',
          backgroundColor: isMenuActive ? bgColor : 'inherit',
        }),

        '.menuIcon': {
          visibility: isMenuActive ? 'visible' : 'hidden',
        },

        '.RegistrationCard__status .text-body': {
          color: textColor ? `${textColor} !important` : undefined,
        },
        ':hover': {
          '.menuIcon': {
            visibility: 'visible',
          },

          backgroundColor: isPendingRegistration ? bgColor : theme.palette.common.lightBg,

          '.MuiTypography-root': {
            color: theme.palette.primary.main,
          },
        },
      },
    };
  },
);

type SameAgeGroupWarningProps = {
  canRollover?: boolean;
  onRollover?: () => void;
};

const SameAgeGroupWarningComponent: FC<PropsWithChildren<SameAgeGroupWarningProps>> = ({
  canRollover,
  onRollover,
}) => {
  const { $t } = useIntl();
  return (
    <Box
      sx={{
        '.MuiIcon-root': {
          paddingTop: 0.5,
        },
      }}
    >
      <SameAgeGroupWarning
        tooltipLabel={
          <Stack gap={1}>
            <Typography>
              {$t(
                { id: 'students-sameAgeGroupWarning' },
                {
                  ageGroup: $t({ id: 'students-sameAgeGroup' }).toLowerCase(),
                },
              )}{' '}
              {$t({ id: 'students-DuringRollover' }).toLowerCase()}
            </Typography>
            {canRollover && (
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRollover?.();
                }}
              >
                <Icon>
                  <ArrowUpIcon />
                </Icon>
                <Typography>
                  <FormattedMessage id="students-RolloverStart" />
                </Typography>
              </Stack>
            )}
          </Stack>
        }
      />
    </Box>
  );
};
